import * as THREE from 'three';
import type { LoopHandle } from '../audio/AudioEngine';
import { CONFIG } from '../config';
import { angleDelta, clamp, damp, lerp } from '../core/math';
import { isTouch } from '../core/device';
import type { Interactions } from '../systems/Interactions';
import type { StageHost } from '../ui/StageHost';
import { buildRascal, RASCAL_FLOOR, type BuiltRascal } from '../world/builders/rascal';
import { inRect, SPOTS, type Rect } from '../world/layout';
import { BAR_SLOT, BOOZE_SLOT, GAS_SLOT, LAUNDRY_SLOT, PAWN_SLOT, roomSlotAt } from '../world/rooms';
import type { Driving } from './Driving';
import type { RestCycle } from './RestCycle';
import type { GameServices } from './services';

/** m/s: walking is 4.2 and Sport Mode 7.6, so "turbo" beats the Crocs. */
const TOP = 5.6;
const TURBO = 8.4;
/** A dead battery still putters along. */
const CRAWL = 2.2;
const REVERSE = 2.2;
const ACCEL = 4;
const BRAKE = 9;
/** Turn rate (rad/s) at speed; it can just about pivot in place. */
const TURN = 2.7;
/** Meters on a full charge (turbo burns it faster). */
const RANGE = 2400;
/** Percent per game hour while it's parked at home, plugged into the porch outlet. */
const CHARGE_RATE = 40;
/** Two circles, nose and tail, keep the whole scooter out of walls. */
const NOSE = { at: 0.55, r: 0.38 };
const TAIL = { at: -0.12, r: 0.38 };
const STILL = { dx: 0, dz: 0, sprint: false, jump: false };
const SHOP_SLOTS = new Set([BAR_SLOT, GAS_SLOT, PAWN_SLOT, BOOZE_SLOT, LAUNDRY_SLOT]);

/** What folks say when you putter past on the Rascal. */
export const RASCAL_BARKS = [
  "Is that a Rascal? You know those are for old folks, right? ...Where'd you get it?",
  'MEEP MEEP!',
  "Watch my toes! I've only got the nine left.",
  'Nice flag. Real visible. Good for you.',
  'Does that thing come in camo?',
  "Speed limit's 5 in the park. You're doin' 6.",
  'Now THAT is a vehicle.',
  "Lemme ride it. I'll bring it right back. Probably.",
  'You can fit a whole twelve-pack in that basket. I respect it.',
];

/**
 * The Lil' Rascal: a mobility scooter you ride anywhere you can walk, including through
 * the front door of the Booze Barn. It's yours once you buy it at Cooter's. It stays
 * where you get off it, charges at home, and you steer it like you walk: it just turns
 * like a vehicle, beeps backing up and goes a little faster than Sport Mode.
 */
export class Rascal {
  readonly model: BuiltRascal;
  riding = false;
  /** Forward speed, m/s (negative backing up). */
  speed = 0;
  private heading = 0;
  private readonly parked = { x: 0, z: 0, heading: 0 };
  private readonly last = { x: 0, z: 0, heading: 0 };
  private colliders: number[] = [];
  private charge = 100;
  private syncTimer = 0;
  private motor: LoopHandle | null = null;
  private beepTimer = 0;
  private steer = 0;
  /** The "where's my Rascal?" arrow shows until this time (seconds). */
  private pointUntil = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();

  constructor(
    private readonly s: GameServices,
    private readonly deps: { driving: Driving; rest: RestCycle; stages: StageHost },
  ) {
    this.model = buildRascal();
    this.model.group.visible = false;
    s.world.dynamic.add(this.model.group);
  }

  get owned(): boolean {
    return this.s.store.has('rascal');
  }

  /** Battery, 0..100. */
  get battery(): number {
    return this.charge;
  }

  /** New game or a load: parked at home with whatever charge it had. */
  reset(): void {
    this.stopRiding();
    const f = this.s.store.flag('rascal_charge');
    this.charge = typeof f === 'number' ? clamp(f, 0, 100) : 100;
    this.parkAtHome();
  }

  /** Overnight it charges, and somebody always brings it home. */
  newDay(): void {
    this.charge = 100;
    this.saveCharge();
    if (this.riding || !this.owned) return;
    const home = this.s.home();
    if (inRect(home.area, this.parked.x, this.parked.z)) return;
    this.parkAtHome();
    this.s.store.toast(
      "Darlene found your Rascal and drove it home. It's on your spot, charged up.",
      'info',
      ':rascal:',
    );
  }

  /** Cooter rolls it out his front door for you. */
  delivered(): void {
    this.charge = 100;
    this.saveCharge();
    this.park(SPOTS.cooterOffice.x - 1.2, SPOTS.cooterOffice.z - 4.2, Math.PI / 2);
  }

  /** Your home moved (a multiplayer park gave you another lot): if it was parked there, so is it. */
  rehome(oldArea: Rect): void {
    if (!this.riding && inRect(oldArea, this.parked.x, this.parked.z)) this.parkAtHome();
  }

  parkAtHome(): void {
    const r = this.s.home().rascal;
    this.park(r.x, r.z, r.rot);
  }

  private park(x: number, z: number, heading: number): void {
    this.parked.x = x;
    this.parked.z = z;
    this.parked.heading = heading;
    this.place(x, z, heading);
    this.clearColliders();
    if (this.owned) {
      const fx = Math.sin(heading);
      const fz = Math.cos(heading);
      for (const c of [NOSE, TAIL])
        this.colliders.push(this.s.world.collision.addCircle(x + fx * c.at, z + fz * c.at, c.r * 0.85));
    }
  }

  private clearColliders(): void {
    for (const id of this.colliders) this.s.world.collision.remove(id);
    this.colliders = [];
  }

  private place(x: number, z: number, heading: number): void {
    const g = this.model.group;
    g.position.set(x, 0, z);
    g.rotation.set(0, heading, 0);
  }

  register(interactions: Interactions): void {
    interactions.add({
      id: 'rascal',
      at: () => this.parked,
      radius: 1.8,
      priority: 0.3,
      prompt: () =>
        this.owned && !this.riding && this.model.group.visible
          ? `Hop on your Lil' Rascal (:battery: ${Math.round(this.charge)}%)`
          : null,
      action: () => this.hopOn(),
      highlight: () => this.model.group,
      icon: ':rascal:',
    });
  }

  /** B (or the hotbar): hop on if it's close, hop off if you're on it, or point at it. */
  toggle(): void {
    const { player, store } = this.s;
    if (!this.owned) {
      store.toast(
        `No Rascal yet. Cooter's got a pre-owned one ($${CONFIG.rascalPrice}), one previous owner.`,
        'info',
        ':rascal:',
      );
      return;
    }
    if (this.riding) {
      this.hopOff();
      return;
    }
    const p = player.position;
    const d = Math.hypot(this.parked.x - p.x, this.parked.z - p.z);
    if (d < 4 && roomSlotAt(p.x) === roomSlotAt(this.parked.x)) {
      this.hopOn();
      return;
    }
    this.pointUntil = this.s.now() + 60;
    const slot = roomSlotAt(this.parked.x);
    store.toast(
      slot >= 0 && slot !== roomSlotAt(p.x)
        ? "You left the Rascal inside somewhere. Retrace your steps (the arrow's on it once you're back out)."
        : `Your Rascal's parked ${Math.round(d)} m that way. Follow the arrow.`,
      'info',
      ':rascal:',
    );
  }

  /** Where the arrow points while you're looking for it. */
  objective(): { text: string; target: THREE.Vector3 } | null {
    if (this.riding || !this.owned || this.s.now() > this.pointUntil) return null;
    if (roomSlotAt(this.s.player.position.x) !== roomSlotAt(this.parked.x)) return null;
    const d = Math.hypot(this.parked.x - this.s.player.position.x, this.parked.z - this.s.player.position.z);
    if (d < 2) {
      this.pointUntil = 0;
      return null;
    }
    return { text: ":rascal: Your Lil' Rascal", target: this.tmp.set(this.parked.x, 0, this.parked.z) };
  }

  hopOn(): void {
    const { player, store, audio } = this.s;
    if (!this.owned || this.riding) return;
    if (this.busy()) {
      store.toast('Kinda busy for a scooter ride right now.', 'info', ':rascal:');
      return;
    }
    this.clearColliders();
    this.riding = true;
    this.speed = 0;
    this.steer = 0;
    this.heading = this.parked.heading;
    player.teleport(this.parked.x, this.parked.z, this.heading);
    player.mount = { seat: seatFor(player.rig.look.height) };
    // Start out looking where it's pointed, so "forward" means forward.
    this.s.cameraRig.yaw = this.heading + Math.PI;
    this.remember();
    this.motor = audio.loop('scooter');
    this.s.sfx('lever');
    if (!store.flag('rascal_tip')) {
      store.setFlag('rascal_tip', true);
      store.toast(
        isTouch()
          ? 'Rascal ON. Joystick to drive (pull back to back up), :croc: for turbo, :hop: to hop off. You can ride it right into the stores.'
          : 'Rascal ON. WASD to drive (S backs up, beep beep), Shift for turbo, H to meep, Space to hop off. Ride it right into the stores.',
        'good',
        ':rascal:',
      );
    }
  }

  /** Step off and leave it parked right here. */
  hopOff(): void {
    if (!this.riding) return;
    const { player, world } = this.s;
    const p = player.position;
    const h = this.heading;
    this.stopRiding();
    this.park(p.x, p.z, h);
    // Step off on the left side (or the right, if there's a wall there).
    for (const side of [1, -1]) {
      const spot = { x: p.x + Math.cos(h) * 0.8 * side, z: p.z - Math.sin(h) * 0.8 * side };
      const want = { ...spot };
      world.collision.resolve(spot, CONFIG.player.radius);
      if (Math.hypot(spot.x - want.x, spot.z - want.z) < 0.25 || side === -1) {
        player.teleport(spot.x, spot.z, h);
        break;
      }
    }
    this.s.sfx('thunk');
  }

  /** Something else needs you (a stage, the couch, your bed): it stays where you were riding. */
  private bail(): void {
    const l = this.last;
    this.stopRiding();
    this.park(l.x, l.z, l.heading);
  }

  private stopRiding(): void {
    if (!this.riding) return;
    this.riding = false;
    this.speed = 0;
    this.s.player.mount = null;
    this.motor?.stop();
    this.motor = null;
    this.saveCharge();
  }

  private saveCharge(): void {
    this.s.store.setFlag('rascal_charge', Math.round(this.charge * 10) / 10);
  }

  private remember(): void {
    const p = this.s.player.position;
    this.last.x = p.x;
    this.last.z = p.z;
    this.last.heading = this.heading;
  }

  /** Can't ride while something else has you: a mini-game, the Camaro, bed, a fridge on your back. */
  private busy(): boolean {
    const { player, stunts } = this.s;
    const b = player.busy;
    return (
      (b !== null && b.pose !== 'drink' && b.pose !== 'eat') ||
      this.deps.stages.isOpen ||
      this.deps.driving.active ||
      this.deps.rest.sleeping ||
      stunts.active ||
      player.carrying !== null ||
      player.pushing !== null ||
      player.hidden ||
      player.scripted
    );
  }

  /** Every frame in play: parking, charging, the flag, getting knocked off by other things. */
  update(dt: number, time: number, night: number): void {
    const { player, store } = this.s;
    const g = this.model.group;
    if (!this.owned) {
      if (this.riding) this.stopRiding();
      if (this.colliders.length) this.clearColliders();
      g.visible = false;
      return;
    }
    if (!this.colliders.length && !this.riding) this.park(this.parked.x, this.parked.z, this.parked.heading);
    if (this.riding && this.busy()) this.bail();
    if (this.riding) g.visible = true;
    else {
      // Rooms are far off past the edge of the park: only draw it where you are.
      g.visible = roomSlotAt(player.position.x) === roomSlotAt(this.parked.x);
      if (this.charge < 100 && inRect(this.s.home().area, this.parked.x, this.parked.z))
        this.charge = Math.min(100, this.charge + (CHARGE_RATE * dt * CONFIG.minutesPerSecond) / 60);
    }
    this.syncTimer -= dt;
    if (this.syncTimer <= 0) {
      this.syncTimer = 5;
      if (Math.abs(Number(store.flag('rascal_charge') ?? 100) - this.charge) >= 1) this.saveCharge();
    }
    // The flag streams out behind you, and flaps either way.
    const going = clamp(Math.abs(this.speed) / TURBO, 0, 1);
    const f = this.model.flag;
    // (Not straight back: a flag streaming dead behind you is edge-on and invisible.)
    f.rotation.y = lerp(-0.55, -1.2, going) + Math.sin(time * (4 + going * 10)) * (0.25 + going * 0.1);
    f.rotation.z = Math.sin(time * 2.3) * 0.05;
    this.model.light.emissiveIntensity = 0.3 + night * 2.2;
  }

  /**
   * Movement while riding (instead of walking). Returns false if you're not riding after
   * all (you hopped off, or something this frame took you), so you walk this frame instead.
   */
  drive(dt: number): boolean {
    const { input, cameraRig, player, world, store, audio } = this.s;
    // Pressing E on a couch or a mini-game just took you: park where you were, don't drive into it.
    if (this.busy()) {
      this.bail();
      return false;
    }
    const time = this.s.now();
    const p = player.position;
    // Went through a door (or got knocked across the road): the scooter comes along.
    if (Math.hypot(p.x - this.last.x, p.z - this.last.z) > 3) {
      this.heading = player.facing;
      this.speed = 0;
    }
    if (input.wasPressed('Space')) {
      this.hopOff();
      return false;
    }
    if (input.wasPressed('KeyH')) {
      audio.play('horn', 1.9);
      window.setTimeout(() => audio.play('horn', 2.1), 170);
    }

    const axis = input.moveAxis();
    const f = cameraRig.forward(this.tmp);
    const r = cameraRig.right(this.tmp2);
    let dx = f.x * axis.y + r.x * axis.x;
    let dz = f.z * axis.y + r.z * axis.x;
    // Drunk driving: the wheel's got a mind of its own.
    const wobble = clamp((store.stat('buzz') - 55) / 45, 0, 1);
    if (wobble > 0 && (dx !== 0 || dz !== 0)) {
      const a = Math.sin(time * 1.9) * 0.5 * wobble;
      const c = Math.cos(a);
      const sn = Math.sin(a);
      const ndx = dx * c - dz * sn;
      dz = dx * sn + dz * c;
      dx = ndx;
    }
    const mag = Math.min(1, Math.hypot(dx, dz));
    const turbo = (input.isDown('ShiftLeft') || input.isDown('ShiftRight')) && this.charge > 0;
    const mud = world.mudAt(p.x, p.z);
    const top = (this.charge <= 0 ? CRAWL : turbo ? TURBO : TOP) * lerp(1, 0.3, mud);
    let target = 0;
    let steer = 0;
    let reversing = false;
    if (mag > 0.08) {
      const want = Math.atan2(dx, dz);
      const diff = angleDelta(this.heading, want);
      if (this.speed < 0.6 && Math.abs(diff) > 2.3) {
        // Pulling back: back it up, tail first, beeping the whole way.
        reversing = true;
        steer = angleDelta(this.heading, want + Math.PI);
        target = -REVERSE * mag;
      } else {
        steer = diff;
        target = top * mag * Math.max(0, Math.cos(diff));
      }
    }
    const rate = TURN * (0.35 + 0.65 * Math.min(1, Math.abs(this.speed) / 2.5));
    const turn = clamp(steer, -rate * dt, rate * dt);
    this.heading += turn;
    this.steer = damp(this.steer, dt > 0 ? turn / dt / TURN : 0, 10, dt);
    if (target > this.speed) this.speed = Math.min(target, this.speed + ACCEL * dt);
    else this.speed = Math.max(target, this.speed - (this.speed > 0 ? BRAKE : ACCEL) * dt);

    const fx = Math.sin(this.heading);
    const fz = Math.cos(this.heading);
    const next = { x: p.x + fx * this.speed * dt, z: p.z + fz * this.speed * dt };
    let bumped = false;
    for (const c of [NOSE, TAIL]) {
      const q = { x: next.x + fx * c.at, z: next.z + fz * c.at };
      if (world.collision.resolve(q, c.r)) {
        next.x = q.x - fx * c.at;
        next.z = q.z - fz * c.at;
        bumped = true;
      }
    }
    if (bumped && Math.abs(this.speed) > 3) {
      this.s.sfx('thud');
      this.s.effects.shake(0.25, 0.2);
      this.speed *= -0.25;
    } else if (bumped) this.speed *= 0.6;
    const b = player.boundsAt(p.x, p.z);
    next.x = clamp(next.x, b.minX + 0.3, b.maxX - 0.3);
    next.z = clamp(next.z, b.minZ + 0.3, b.maxZ - 0.3);
    const moved = Math.hypot(next.x - p.x, next.z - p.z);
    p.x = next.x;
    p.z = next.z;
    player.facing = this.heading;

    this.charge = Math.max(0, this.charge - (moved / RANGE) * 100 * (turbo ? 2.2 : 1));
    if (this.charge <= 15 && !store.doneToday('rascal_low')) {
      store.markToday('rascal_low');
      store.toast(
        'Rascal battery at 15%. Park it at home to charge (or it putters).',
        'bad',
        ':battery-low:',
      );
    }
    if (reversing && this.speed < -0.2) {
      this.beepTimer -= dt;
      if (this.beepTimer <= 0) {
        this.beepTimer = 0.55;
        audio.beep(1150, 0.2);
      }
    } else this.beepTimer = 0;
    if (wobble > 0.4 && Math.abs(this.speed) > 2) store.unlock('rascal_dui');
    if (SHOP_SLOTS.has(roomSlotAt(p.x))) store.unlock('rascal_shop');

    player.update(dt, time, STILL, world.collision, {
      buzz: store.stat('buzz'),
      energy: store.stat('energy'),
      poseOverride: 'scoot',
    });
    this.place(p.x, p.z, this.heading);
    this.model.tiller.rotation.y = this.steer * 0.45;
    const m = this.model;
    for (const w of m.wheels) w.rotation.y += (this.speed * dt) / 0.12;
    m.group.rotation.z = -this.steer * Math.min(1, Math.abs(this.speed) / TURBO) * 0.08;
    this.motor?.set(Math.abs(this.speed) / TURBO);
    this.remember();
    return true;
  }

  /** For the camera: follow behind when you're rolling forward. */
  get followYaw(): number | undefined {
    return this.riding && this.speed > 1.2 ? this.heading + Math.PI : undefined;
  }
}

/**
 * The seat height to hand the animator so your feet land on the floorboard whatever your
 * height (the rig scales with it; legs are 0.47 m hip to sole when seated like this).
 */
export function seatFor(height: number): number {
  return (RASCAL_FLOOR + 0.47 * height) / height - 0.11;
}
